import { useUserSession } from 'features/session/context'

import { DashBoardProfileResult } from 'interface/dashBoardProfile'
import { SearchDetailResult } from 'interface/search'

import {
  Box,
  Popover,
  List,
  Divider,
  Typography,
  ListSubheader,
  ListItem,
  ListItemButton,  
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material'
import HandshakeIcon from '@mui/icons-material/Handshake';

import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'
import { ErrorDialog } from 'components/dialog/error'
import { MessageDialog } from 'components/dialog/message'
import { ConfirmDialog } from 'components/dialog/confirm'

import { useDashBoardProfileList } from './container/useDashBoardProfileList'

import { TrushIcon } from 'shared/icons'
import { useState } from 'react';

type LoadDashBoardProfilePopoverProps = {
  anchorEl: HTMLButtonElement | null
  profileKey: string,
  search: SearchDetailResult | null
  onClick: (profile: DashBoardProfileResult) => void
  onDeleteProfileReply: (profileId: number) => void
  onClose: () => void
}

export const LoadDashBoardProfilePopover = ({
  anchorEl,
  profileKey,
  search,
  onClick,
  onDeleteProfileReply,
  onClose
}: LoadDashBoardProfilePopoverProps) => {
  const { userId } = useUserSession()
  const {
    isLoading,
    error,
    bySearchProfiles,
    byUserProfiles,
    sharedProfiles,
    deleteProfileState,
    deleteProfile,
  } = useDashBoardProfileList({
    profileKey,
    search,
    userId,
    onDeleteProfileReply
  })
  const [deleteTarget, setDeleteTarget] = useState<DashBoardProfileResult | null>(null)

  if (isLoading) return <Loading/>
  if (error) return <ErrorDialog isOpen={true} error={error} onClose={onClose}/>
  if (!bySearchProfiles || !byUserProfiles || !sharedProfiles) return <></>
  if (
    (bySearchProfiles.length === 0) &&
    (byUserProfiles.length === 0) &&
    (sharedProfiles.length === 0)
  ) {
    return <MessageDialog message='データが存在しません' onClose={onClose}/>
  }
  return (
    <Popover
      id="load-profile-popover"
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      sx={{ maxHeight: '70%' }}
    >
      <Box>
        {deleteProfileState.posting && <Loading/>}

        <ErrorBlock error={deleteProfileState.error}/>

        {deleteTarget && (
          <ConfirmDialog
            message={`${deleteTarget.profileName} を削除します。よろしいですか？`}
            ok={{
              label: 'はい',
              onClick: () => {
                deleteProfile(deleteTarget.profileId)
                setDeleteTarget(null)
              }
            }}
            cancel={{
              label: 'いいえ',
              onClick: () => {
                setDeleteTarget(null)
              }
            }}
          />
        )}
        <List
          sx={{
            width: '100%',
            maxWidth: '30em',
            bgcolor: 'background.paper',
            position: 'relative',
            overflow: 'auto',
            maxHeight: '20em',
            '& ul': { padding: 0 },
            paddingY: 0
          }}
        >
          {[
            {
              title: `${search?.title} 専用`,
              profiles: bySearchProfiles
            },
            {
              title: "個人用",
              profiles: byUserProfiles
            },
            {
              title: "ユーザー共有",
              profiles: sharedProfiles
            }
          ].map((section, i) => (section.profiles && section.profiles.length > 0) ? (
            <>
              <ListSubheader disableGutters key={`profile-group-${i}`}>
                <Typography variant="body1" paddingX={2} paddingY={1}>{section.title}</Typography>
                <Divider/>
              </ListSubheader>

              {section.profiles.map((profile, j) => (
                <ListItem
                  key={`profile-${profile.profileId}-${i}-${j}`}
                  disableGutters
                  disablePadding
                  secondaryAction={
                    profile.userId === userId ? (
                      <IconButton aria-label="delete" onClick={() => setDeleteTarget(profile)}>
                        <TrushIcon />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <ListItemButton onClick={() => onClick(profile)}>
                    <ListItemIcon sx={{ minWidth: '2em' }}>
                      {(profile.isShared && profile.userId === userId) ? (
                        <HandshakeIcon color="success"/>
                      ) : (
                        <HandshakeIcon color="disabled"/>
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={profile.profileName}
                      sx={{
                        flexGrow: 1,
                        minWidth: '20em',
                        wordBreak: 'break-all'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          ) : <></>)}
        </List>
      </Box>
    </Popover>
  )
}
